import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch, http } from "@/lib/api";
import { PageHeader, Loading, SectionTitle, EmptyState } from "@/components/common";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";
import { Bell, Plus, Trash2, Zap } from "lucide-react";
import { useStream } from "@/lib/useStream";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const TEMPLATES = [
  "Token under $250K with Momentum > 75 and ≥ 2 tracked wallets buying",
  "A tracked wallet buys a new token",
  "A tracked developer launches a token",
  "Developer wallet starts selling",
  "Liquidity falls sharply (> 40%)",
  "Security risk score changes significantly",
  "Holder concentration changes significantly",
  "A narrative suddenly accelerates",
];

export default function Alerts() {
  const { data: alerts, reload } = useFetch("/alerts", []);
  const { data: feed } = useFetch("/alerts/feed", []);
  const { events: liveEvents, connected } = useStream(25);
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [tpl, setTpl] = useState(TEMPLATES[0]);
  const [channels, setChannels] = useState({ in_app: true, browser: false, email: false, webhook: false });
  const [open, setOpen] = useState(false);

  const create = async () => {
    const ch = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    await http.post("/alerts", { name: name || tpl, conditions: [{ rule: tpl }], channels: ch });
    toast.success("Alert created");
    setName("");
    setOpen(false);
    reload();
  };
  const toggle = async (a) => { await http.patch(`/alerts/${a.id}?enabled=${!a.enabled}`); reload(); };
  const del = async (id) => { await http.delete(`/alerts/${id}`); toast.success("Alert deleted"); reload(); };

  return (
    <div className="fade-in">
      <PageHeader
        icon={Bell}
        title="Alerts Engine"
        subtitle="Build compound alert conditions. Supports in-app & browser now; email and webhooks later."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-alert-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-amber-50 hover:bg-amber-500">
                <Plus className="h-4 w-4" /> New Alert
              </button>
            </DialogTrigger>
            <DialogContent className="bg-panel border-slate-700">
              <DialogHeader><DialogTitle className="font-display">Create Alert</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <input data-testid="alert-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alert name (optional)" className="w-full rounded-lg border border-slate-800 bg-void px-3 py-2 text-sm text-slate-200 outline-none focus:border-amber-600/50" />
                <div>
                  <div className="mb-1 font-mono text-[10px] uppercase text-slate-500">Condition</div>
                  <select data-testid="alert-template" value={tpl} onChange={(e) => setTpl(e.target.value)} className="w-full rounded-lg border border-slate-800 bg-void px-3 py-2 text-sm text-slate-200 outline-none">
                    {TEMPLATES.map((t) => (<option key={t} value={t}>{t}</option>))}
                  </select>
                </div>
                <div>
                  <div className="mb-1.5 font-mono text-[10px] uppercase text-slate-500">Channels</div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.keys(channels).map((c) => (
                      <label key={c} className="flex items-center justify-between rounded-md border border-slate-800 bg-void px-3 py-2 text-xs text-slate-300">
                        <span className="capitalize">{c.replace("_", "-")}{(c === "email" || c === "webhook") && <span className="ml-1 text-[9px] text-slate-600">(later)</span>}</span>
                        <Switch checked={channels[c]} onCheckedChange={(v) => setChannels({ ...channels, [c]: v })} disabled={c === "email" || c === "webhook"} />
                      </label>
                    ))}
                  </div>
                </div>
                <button data-testid="alert-create-submit" onClick={create} className="w-full rounded-lg bg-amber-600 py-2 text-sm font-semibold text-amber-50 hover:bg-amber-500">Create Alert</button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <div>
          <SectionTitle>Active Alerts ({alerts?.data?.length || 0})</SectionTitle>
          {!alerts ? <Loading /> : alerts.data.length === 0 ? (
            <EmptyState icon={Bell} title="No alerts yet" hint="Create a compound alert to get notified when conditions are met." />
          ) : (
            <div className="space-y-3">
              {alerts.data.map((a) => (
                <div key={a.id} className="pnut-card flex items-center justify-between p-4">
                  <div>
                    <div className="text-sm font-semibold text-slate-100">{a.name}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{a.conditions?.[0]?.rule}</div>
                    <div className="mt-1 flex gap-1">
                      {a.channels?.map((c) => (<span key={c} className="rounded border border-slate-800 px-1.5 py-0.5 font-mono text-[9px] text-slate-400">{c}</span>))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={a.enabled} onCheckedChange={() => toggle(a)} data-testid={`alert-toggle-${a.id}`} />
                    <button onClick={() => del(a.id)} className="rounded-md p-1.5 text-slate-500 hover:bg-rose-950/40 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <SectionTitle right={<span className="inline-flex items-center gap-1.5 font-mono text-[10px] text-slate-500"><span className={`live-dot ${connected ? "" : "!bg-slate-600"}`} style={connected ? {} : { animation: "none" }} />{connected ? "live · websocket" : "reconnecting…"}</span>}>Live Alert Feed</SectionTitle>
          <div className="space-y-2" data-testid="alerts-live-feed">
            {liveEvents.map((f) => (
              <button key={f.id} onClick={() => f.token_id && nav(`/token/${f.token_id}`)} className={`fade-in flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left ${f.severity === "high" ? "border-amber-700/40 bg-amber-950/10" : "border-slate-800 bg-panel"}`}>
                <Zap className={`h-4 w-4 ${f.severity === "high" ? "text-amber-400" : "text-slate-500"}`} />
                <div className="flex-1">
                  <div className="text-xs text-slate-200">{f.title}</div>
                  <div className="text-[10px] text-slate-500">just now{f.live ? " · LIVE" : ""}</div>
                </div>
              </button>
            ))}
            {feed?.data?.map((f) => (
              <button key={f.id} onClick={() => nav(`/token/${f.token_id}`)} className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left ${f.severity === "high" ? "border-amber-700/40 bg-amber-950/10" : "border-slate-800 bg-panel"}`}>
                <Zap className={`h-4 w-4 ${f.severity === "high" ? "text-amber-400" : "text-slate-500"}`} />
                <div className="flex-1">
                  <div className="text-xs text-slate-200">{f.title}</div>
                  <div className="text-[10px] text-slate-500">{timeAgo(f.time_min_ago)} ago</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
